import { useState, useEffect, useRef } from 'react';
import { Music, Play, Pause, Check, X, ChevronDown, Clock } from 'lucide-react';
import { useThemeClasses } from '../hooks/useTheme';
import { useAuth } from '../context/AuthContext';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

function formatDuration(s) {
  if (!s) return '--:--';
  return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;
}

const STATUS_COLORS = {
  pending: 'text-amber-500 bg-amber-500/10',
  approved: 'text-green-500 bg-green-500/10',
  rejected: 'text-red-400 bg-red-400/10',
};

export default function AdminMusicPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isDark, textClass, textMutedClass, borderClass } = useThemeClasses();
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [playingId, setPlayingId] = useState(null);
  const [noteMap, setNoteMap] = useState({});
  const [actionLoading, setActionLoading] = useState({});
  const audioRef = useRef(null);

  useEffect(() => {
    if (user && !user.is_admin) navigate('/home');
  }, [user, navigate]);

  useEffect(() => {
    fetchTracks();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const fetchTracks = async () => {
    setLoading(true);
    try {
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const res = await axios.get(`${API}/api/music/admin/submissions`, { params, withCredentials: true });
      setTracks(res.data.tracks || []);
    } catch (e) {
      toast.error('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };

  const handlePlay = (track) => {
    if (playingId === track.track_id) {
      audioRef.current?.pause();
      setPlayingId(null);
      return;
    }
    if (audioRef.current) {
      audioRef.current.pause();
    }
    const audio = new Audio(track.file_url);
    audioRef.current = audio;
    audio.play().catch(() => toast.error('Could not play audio'));
    audio.onended = () => setPlayingId(null);
    setPlayingId(track.track_id);
  };

  const handleAction = async (track, action) => {
    setActionLoading(m => ({ ...m, [track.track_id]: true }));
    try {
      const note = noteMap[track.track_id] || '';
      await axios.patch(
        `${API}/api/music/admin/${track.track_id}/status`,
        null,
        {
          params: { action, note: note || undefined },
          withCredentials: true,
        }
      );
      toast.success(action === 'approved' ? '✅ Track approved and live!' : 'Track rejected');
      setTracks(prev => prev.filter(t => t.track_id !== track.track_id));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally {
      setActionLoading(m => ({ ...m, [track.track_id]: false }));
    }
  };

  const inputClass = cn(
    "w-full px-3 py-2 text-xs border outline-none transition-colors",
    isDark ? 'bg-zinc-900 border-white/10 text-white placeholder:text-white/20' : 'bg-white border-gray-200 text-gray-900 placeholder:text-gray-400'
  );

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 pb-24">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Music className="h-5 w-5 text-amber-500" />
          <h1 className={cn("text-xl font-display tracking-wider", textClass)}>Music Submissions</h1>
        </div>
        <div className="flex gap-1.5">
          {['pending', 'approved', 'rejected', 'all'].map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={cn(
                "px-3 py-1.5 text-[10px] font-display tracking-wider uppercase transition-colors",
                statusFilter === s
                  ? 'bg-amber-500 text-black'
                  : isDark ? 'text-white/40 hover:text-white' : 'text-gray-400 hover:text-gray-900'
              )}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="py-16 text-center">
          <div className="animate-spin w-5 h-5 border-2 border-amber-500 border-t-transparent rounded-full mx-auto" />
        </div>
      ) : tracks.length === 0 ? (
        <div className={cn("py-16 text-center", textMutedClass)}>
          <Music className="h-8 w-8 mx-auto mb-3 opacity-40" />
          <p className="text-sm">No {statusFilter !== 'all' ? statusFilter : ''} submissions</p>
        </div>
      ) : (
        <div className="space-y-4">
          {tracks.map(track => (
            <div
              key={track.track_id}
              className={cn("p-4 border rounded-xl", isDark ? 'border-white/10 bg-zinc-900' : 'border-gray-200 bg-white')}
            >
              {/* Track header */}
              <div className="flex items-start gap-3 mb-3">
                <div className="w-14 h-14 rounded-xl overflow-hidden shrink-0 bg-amber-500/10 flex items-center justify-center">
                  {track.cover_art_url ? (
                    <img src={track.cover_art_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <Music className="h-6 w-6 text-amber-500" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className={cn("text-base font-medium truncate", textClass)}>{track.title}</span>
                    <span className={cn("text-[10px] px-2 py-0.5 rounded-full font-display tracking-wider uppercase shrink-0", STATUS_COLORS[track.status])}>
                      {track.status}
                    </span>
                  </div>
                  <p className={cn("text-sm", textMutedClass)}>by {track.stage_name}</p>
                  <div className="flex items-center gap-3 mt-1">
                    {track.duration_seconds && (
                      <span className={cn("text-xs flex items-center gap-1", textMutedClass)}>
                        <Clock className="h-3 w-3" />
                        {formatDuration(track.duration_seconds)}
                      </span>
                    )}
                    <div className="flex gap-1">
                      {(track.genre_tags || []).map(g => (
                        <span key={g} className={cn("text-[10px] font-display tracking-wider uppercase", textMutedClass)}>{g}</span>
                      ))}
                    </div>
                  </div>
                </div>
                {/* Play button */}
                <button
                  onClick={() => handlePlay(track)}
                  className={cn(
                    "w-9 h-9 rounded-full flex items-center justify-center shrink-0 transition-colors",
                    playingId === track.track_id
                      ? 'bg-amber-500 text-black'
                      : isDark ? 'bg-white/10 text-white hover:bg-white/20' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  )}
                >
                  {playingId === track.track_id ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                </button>
              </div>

              {/* Submitted at */}
              <p className={cn("text-[10px] mb-3", textMutedClass)}>
                Submitted {new Date(track.submitted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </p>

              {/* Note field + action buttons — only for pending */}
              {track.status === 'pending' && (
                <div className="space-y-2">
                  <input
                    className={inputClass}
                    placeholder="Optional note to artist..."
                    value={noteMap[track.track_id] || ''}
                    onChange={e => setNoteMap(m => ({ ...m, [track.track_id]: e.target.value }))}
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleAction(track, 'approved')}
                      disabled={actionLoading[track.track_id]}
                      className={cn(
                        "flex-1 flex items-center justify-center gap-2 py-2.5 text-xs font-display tracking-wider uppercase transition-colors",
                        "bg-green-600 text-white hover:bg-green-500 disabled:opacity-50"
                      )}
                    >
                      <Check className="h-3.5 w-3.5" />
                      Approve
                    </button>
                    <button
                      onClick={() => handleAction(track, 'rejected')}
                      disabled={actionLoading[track.track_id]}
                      className={cn(
                        "flex-1 flex items-center justify-center gap-2 py-2.5 text-xs font-display tracking-wider uppercase transition-colors",
                        isDark
                          ? 'border border-white/20 text-white/60 hover:text-white disabled:opacity-50'
                          : 'border border-gray-200 text-gray-500 hover:text-gray-900 disabled:opacity-50'
                      )}
                    >
                      <X className="h-3.5 w-3.5" />
                      Reject
                    </button>
                  </div>
                </div>
              )}

              {/* Admin note display for reviewed tracks */}
              {track.admin_note && track.status !== 'pending' && (
                <p className={cn("text-xs mt-2 italic", textMutedClass)}>
                  Note: "{track.admin_note}"
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
